import { Box, Radio, Text } from '@factorialco/deprecated-design-system'
import { FormikErrors, FormikProps } from 'formik'
import { CardholderInformationDocument } from 'generated/resources/graphql'

import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import countryList from 'modules/core/lib/countryList'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import DialCodeSelector from 'modules/expenses/components/common/DialCodeSelector'
import { parsePhoneNumber } from 'modules/expenses/lib/phoneNumber'

import { SectionHeader } from './SectionHeader'
import { CreateCardForm } from './useCardForm'

interface Props {
  form: FormikProps<CreateCardForm>
}
const setValue = (
  field: keyof CreateCardForm['shipping'],
  value: string,
  form: Props['form'],
  shouldValidate = false
) => {
  form.setValues(
    {
      ...form.values,
      shipping: {
        ...form.values.shipping,
        [field]: value,
      },
    },
    shouldValidate
  )
}

const getError = (field: keyof FormikErrors<CreateCardForm['shipping']>, form: Props['form']) => {
  return form.touched.shipping?.[field] ? form.errors.shipping?.[field] : undefined
}

const setTouched = (
  field: keyof CreateCardForm['shipping'],
  form: Props['form'],
  shouldValidate = false
) => {
  form.setTouched(
    {
      ...form.touched,
      shipping: { ...form.touched.shipping, [field]: true },
    },
    shouldValidate
  )
}

export const ShippingForm = ({ form }: Props) => {
  const locale = useCurrentSession().access.locale
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const countryOptions = countryList[i18n.locale as keyof typeof countryList] || countryList.en

  const [fetchContactPerson] = useLazyQuery(CardholderInformationDocument, {
    onCompleted(data) {
      const employee = data.employees.employee

      if (employee) {
        const rawPhone = employee.phoneNumber ?? undefined
        const parsedPhone = parsePhoneNumber(rawPhone)
        const phoneNumber =
          parsedPhone && parsedPhone.getNationalNumber()
            ? parsedPhone.getNationalNumber()
            : rawPhone
        const phoneCountryCode =
          parsedPhone && parsedPhone.getCountryCode() ? `+${parsedPhone.getCountryCode()}` : ''
        const shippingInfo = {
          companyPhoneNumber: phoneNumber?.toString(),
          companyPhoneCountryCode: phoneCountryCode,
          companyEmployeeFullName: employee.fullName,
        }

        form.setValues({
          ...form.values,
          shipping: {
            ...form.values.shipping,
            ...shippingInfo,
          },
        })
      }
    },
  })

  useUnsafeEffect(() => {
    if (form.values.shippingContactPerson) {
      fetchContactPerson({
        variables: {
          employeeId: form.values.shippingContactPerson,
          creationSource: 'expenses',
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.shippingContactPerson])

  const isShippingContactPersonDirty =
    form.getFieldMeta('shippingContactPerson')?.initialValue !== form.values.shippingContactPerson

  return (
    <Box gap="s48" width="full">
      <Box gap="s16" width="full">
        <SectionHeader title={i18n.t('expenses_v2.forms.fields.header_shipping.title')} />
        <Box flexDirection="row" gap="s16" justifyContent="spaceBetween">
          <Radio.Wrap key={'cardholder'}>
            <Radio
              name={'sendingType'}
              value={form.values.shipping.sendingType}
              onChange={(value) => {
                setValue('sendingType', value, form)
                setTouched('sendingType', form, true)
              }}
              option={{
                value: 'cardholder',
                label: i18n.t('expenses_v2.forms.fields.sending_options.cardholder'),
              }}
              description={i18n.t(
                'expenses_v2.forms.fields.sending_options.cardholder_description'
              )}
            />
          </Radio.Wrap>
          <Radio.Wrap key={'company'}>
            <Radio
              name={'sendingType'}
              value={form.values.shipping.sendingType}
              onChange={(value) => {
                setValue('sendingType', value, form)
                setTouched('sendingType', form, true)
              }}
              option={{
                value: 'company',
                label: i18n.t('expenses_v2.forms.fields.sending_options.company'),
              }}
              description={i18n.t('expenses_v2.forms.fields.sending_options.company_description')}
            />
          </Radio.Wrap>
        </Box>
        {form.values.shipping.sendingType === 'company' && (
          <>
            <Box gap="s6">
              <EmployeeSelectorV2
                onChange={(selectedEmployeId) => {
                  // We don't want to validate here as it will be done by the hook in a useEffect
                  form.setFieldTouched('shippingContactPerson', true, false)
                  form.setFieldValue('shippingContactPerson', selectedEmployeId ?? undefined, true)
                }}
                mandatory
                label={i18n.t('expenses_v2.forms.fields.sending_options.contact_person')}
                error={isShippingContactPersonDirty ? form.errors.shippingContactPerson : ''}
                value={form.values.shippingContactPerson}
              />
              <Text color="grey700" size="200">
                {i18n.t('expenses_v2.forms.fields.sending_options.contact_person_example')}
              </Text>
            </Box>
            <Box flexDirection="row" gap="s16">
              <Box gap="s6" width="fit-content" flexNoShrink>
                <DialCodeSelector
                  label={i18n.t(
                    'software_management.initial_setup.wizard.cardholder_form.country_code'
                  )}
                  onChange={(value) => {
                    setValue('companyPhoneCountryCode', value, form, true)
                    setTouched('companyPhoneCountryCode', form, false)
                  }}
                  onClose={() => setTouched('companyPhoneCountryCode', form, true)}
                  value={form.values.shipping.companyPhoneCountryCode}
                  error={getError('companyPhoneCountryCode', form)}
                  locale={locale}
                  mandatory
                />
                <Text color="grey700" size="200">
                  {i18n.t('expenses_v2.forms.fields.sending_options.country_phone_number_example')}
                </Text>
              </Box>
              <Box gap="s6" width="full">
                <TextField
                  label={i18n.t(
                    'software_management.initial_setup.wizard.cardholder_form.phone_number'
                  )}
                  onChange={(value) => setValue('companyPhoneNumber', value, form)}
                  onBlur={(ev) => {
                    setTouched('companyPhoneNumber', form)
                    form.handleBlur(ev)
                  }}
                  value={form.values.shipping.companyPhoneNumber || ''}
                  mandatory
                  error={getError('companyPhoneNumber', form)}
                />
                <Text color="grey700" size="200">
                  {i18n.t('expenses_v2.forms.fields.sending_options.company_phone_number_example')}
                </Text>
              </Box>
            </Box>
            <Box gap="s6">
              <TextField
                mandatory
                label={i18n.t('expenses_v2.forms.fields.sending_options.company_name')}
                onChange={(value) => setValue('companyName', value, form)}
                onBlur={(ev) => {
                  setTouched('companyName', form)
                  form.handleBlur(ev)
                }}
                value={form.values.shipping.companyName || ''}
                error={getError('companyName', form)}
              />
              <Text color="grey700" size="200">
                {i18n.t('expenses_v2.forms.fields.sending_options.company_name_example')}
              </Text>
            </Box>
          </>
        )}
        <Box gap="s6">
          <TextField
            mandatory
            label={i18n.t('expenses_v2.forms.fields.address_line_1.label')}
            placeholder={i18n.t('expenses_v2.forms.fields.address_line_1.placeholder')}
            onChange={(value) => setValue('addressLine1', value, form)}
            onBlur={(ev) => {
              setTouched('addressLine1', form)
              form.handleBlur(ev)
            }}
            value={form.values.shipping.addressLine1}
            error={getError('addressLine1', form)}
          />
          <Text color="grey700" size="200">
            {i18n.t('expenses_v2.forms.fields.address_line_1.example')}
          </Text>
        </Box>
        <Box gap="s6">
          <TextField
            label={i18n.t('expenses_v2.forms.fields.address_line_2.label')}
            placeholder={i18n.t('expenses_v2.forms.fields.address_line_2.placeholder')}
            onChange={(value) => setValue('addressLine2', value, form)}
            onBlur={(ev) => {
              setTouched('addressLine2', form)
              form.handleBlur(ev)
            }}
            value={form.values.shipping.addressLine2 || ''}
            error={getError('addressLine2', form)}
          />
          <Text color="grey700" size="200">
            {i18n.t('expenses_v2.forms.fields.address_line_2.example')}
          </Text>
        </Box>
        <TextField
          mandatory
          label={i18n.t('expenses_v2.forms.fields.state.label')}
          onChange={(value) => setValue('state', value, form)}
          onBlur={(ev) => {
            setTouched('state', form)
            form.handleBlur(ev)
          }}
          value={form.values.shipping.state || ''}
          error={getError('state', form)}
        />
        <TextField
          mandatory
          label={i18n.t('expenses_v2.forms.fields.city.label')}
          onChange={(value) => setValue('city', value, form)}
          onBlur={(ev) => {
            setTouched('city', form)
            form.handleBlur(ev)
          }}
          value={form.values.shipping.city}
          error={getError('city', form)}
        />
        <TextField
          mandatory
          label={i18n.t('expenses_v2.forms.fields.postal_code.label')}
          onChange={(value) => setValue('postalCode', value, form)}
          onBlur={(ev) => {
            setTouched('postalCode', form)
            form.handleBlur(ev)
          }}
          value={form.values.shipping.postalCode}
          error={getError('postalCode', form)}
        />
        <Select
          none={i18n.t('common.choose')}
          options={countryOptions}
          label={i18n.t('expenses_v2.forms.fields.country.label')}
          onChange={(value) => {
            setValue('country', value, form, true)
            setTouched('country', form)
          }}
          onBlur={(ev) => {
            setTouched('country', form)
            form.handleBlur(ev)
          }}
          value={form.values.shipping.country}
          error={getError('country', form)}
        />
      </Box>
    </Box>
  )
}
